// Libs
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import {
    deadlineTimezoneAtom,
    optionsAtom
} from '@/components/pages/giveaway-picker/Atoms/OptionsAtoms';
// Atoms
import { currentStepAtom } from '@/components/pages/giveaway-picker/Atoms/stepsAtoms';
// Components
import { SummaryItemTitle } from '@/components/pages/giveaway-picker/GiveawayPickerWrapper/components/DrawSummary/components/SummaryItemTitle';

type SummaryItemProps = {
    id: string;
    label: string;
    value: React.ReactNode;
};

const SummaryItem = ({ id, label, value }: SummaryItemProps) => {
    return (
        <div className="flex items-center justify-between" data-testid={`summary-item-${id}`}>
            <span className="text-left font-GMedium text-sm text-icoGray-500">{label}:</span>
            <span className="font-GMedium text-sm text-icoBlack">{value}</span>
        </div>
    );
};

export default function SummaryItemThirdStep() {
    const { t } = useTranslation('giveaway_picker');
    const currentStep = useAtomValue(currentStepAtom);
    const options = useAtomValue(optionsAtom);
    const timezone = useAtomValue(deadlineTimezoneAtom);

    if (currentStep < 3) {
        return null;
    }

    const summaryItems = [
        {
            id: 'deadline',
            label: t('options.deadline'),
            value: options.deadline
                ? dayjs(options.deadline).tz(timezone).format('MM/DD/YYYY hh:mm A Z')
                : t('summary_item.no_deadline')
        },
        {
            id: 'number-of-winners',
            label: t('options.summary.number_of_winners'),
            value: options.nb_winners
        },
        {
            id: 'alternative-winners',
            label: t('options.alternative_winners'),
            value: options.nb_extraWinners ?? t('summary_item.no_alternative_winners')
        },
        {
            id: 'hashtags',
            label: t('options.hashtags'),
            value:
                options.hashtags && options.hashtags.length > 0
                    ? options.hashtags.join(' ')
                    : t('summary_item.no_hashtags')
        },
        {
            id: 'number-of-mentions',
            label: t('options.number_of_mentions'),
            value: options.nb_mentions ?? t('options.no_mentions')
        },
        {
            id: 'comments-on-promoted-posts',
            label: t('options.comments_on_promoted_posts'),
            value: options.paid_comments ? t('commons:yes') : t('commons:no')
        },
        {
            id: 'only-unique-comments',
            label: t('options.only_unique_comments'),
            value: options.only_unique_comments ? t('commons:yes') : t('commons:no')
        }
    ];

    return (
        <div data-testid="summary-item-3" className="flex flex-col gap-4 text-right">
            <SummaryItemTitle step={2} />
            {summaryItems.map((item) => (
                <SummaryItem key={item.id} id={item.id} label={item.label} value={item.value} />
            ))}
        </div>
    );
}
